import React, { useState, useEffect, useRef } from "react";
import moment from "moment-timezone";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Radio,
  RadioGroup,
  Select,
  Spacer, Spinner,
  Stack,
  Switch,
  Text,
  Textarea,
  VStack
} from "@chakra-ui/react";
import CreatableSelect from "react-select";
import AsyncSelect from "react-select/async";
import ReactDatePicker from "react-datepicker";
import CampaignTypes from "./CampaignTypes";
import DripStepsEditor from "./DripStepsEditor";


const DebouncedInput = ({ value: externalValue, onChange, delay = 300, ...props }) => {
  const [localValue, setLocalValue] = useState(externalValue ?? "");
  const timeoutRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const latestValueRef = useRef(externalValue ?? "");
  const lastEmittedRef = useRef(externalValue ?? "");
  onChangeRef.current = onChange;

  useEffect(() => {
    const ext = externalValue ?? "";
    // Only sync from parent if this is a genuinely external change,
    // not an echo of a value we emitted ourselves
    if (ext !== lastEmittedRef.current) {
      setLocalValue(ext);
      latestValueRef.current = ext;
    }
  }, [externalValue]);

  const emit = (value) => {
    lastEmittedRef.current = value;
    onChangeRef.current(value);
  };

  const flush = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      emit(latestValueRef.current);
    }
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    latestValueRef.current = newValue;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      emit(newValue);
    }, delay);
  };

  useEffect(() => {
    return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
  }, []);

  return <Input value={localValue} onChange={handleChange} onBlur={flush} {...props} />;
};

const CampaignSettings = ({
                            medium,
                            template,
                            campaignType,
                            getTemplateValue,
                            handleSettingChange,
                            settingBgColor,
                            filterOptionEnabled,
                            setFilterOptionEnabled,
                            toast,
                            colorMode,
                            isEditing,
                            loadOptions,
                            onBackClicked,
                            onCancelClicked,
                            onSaveClicked,
                            savingCampaign,
                            timezone,
                            handleFocus,
                            handleBlur,
                            facebookCampaignOptions = [],
                            campaigns = [],
                            reminderExcludedOperatoriesCount,
                          }) => {
  const [arrayInputValues, setArrayInputValues] = useState({});
  const isDefaultLeadAdsCampaign = (campaign) => (
    campaign.purpose === "lead-ads"
    && !campaign.archived
    && (
      campaign.source?.is_default === true
      || !campaign.source?.campaign_id
    )
  );

  const getArrayInputValue = (filterId) => arrayInputValues[filterId] ?? "";
  const setArrayInputValue = (filterId, value) =>
    setArrayInputValues((prev) => ({ ...prev, [filterId]: value }));
  const normalizeMultiValue = (v) => (Array.isArray(v) ? v : []);

  const campaignTypeConfig = CampaignTypes.find((c) => c.id === campaignType) || {};
  const filters = campaignTypeConfig.filters || [];

  const isRequired = (filter) => {
    if (filter.required === true) return true;
    if (typeof filter.required === "function") {
      return !!filter.required(medium, template);
    }
    return false;
  };

  const handleNewArrayValue = (newValue, filter) => {
    if (!newValue) return "";
    if (filter.pattern?.pattern && !filter.pattern.pattern.test(newValue)) {
      toast({
        title: "Invalid Input",
        description: filter.pattern.invalid || "The input does not match the required format.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return "";
    }

    const newOption = { value: newValue, label: newValue };
    const existing = normalizeMultiValue(getTemplateValue(filter.id, template));

    // avoid duplicates
    if (existing.some((o) => o?.value === newValue)) {
      setArrayInputValue(filter.id, "");
      return "";
    }

    handleSettingChange(filter.id, [...existing, newOption]);
    setArrayInputValue(filter.id, "");
    return "";
  };
  
  const dateFromDatePicker = (date) => {
    const stringDate = `${date.toLocaleDateString('en-US')} ${date.toLocaleTimeString('en-US')}`.toLowerCase();
    const inTZ = moment.tz(stringDate, 'MM/DD/YYYY hh:mm:ss A', timezone);
    return inTZ.toISOString();
  };
  
  const dateToDatePicker = (isoString) => {
    const inTZ = moment.tz(isoString, timezone);

    const local = moment(inTZ.format("YYYY-MM-DDTHH:mm:ss"));
    
    return local.toDate();
  };

  return (
    <VStack
      spacing={4}
      align="start"
      padding="20px"
      className="campaign_settings"
    >
      {filters
        .filter((x) => !x.condition || x.condition({ medium, template }))
        .map((filter) => {
          if (["array", "toggle_array"].includes(filter.type)) {
            const currentValue = normalizeMultiValue(getTemplateValue(filter.id, template));
            const hasOptions = Array.isArray(filter.options) && filter.options.length > 0;
            const inputValue = getArrayInputValue(filter.id);
            const selectOptions = hasOptions
              ? filter.options.map((o) => ({ value: o.value, label: o.name }))
              : undefined;

            return (
              <FormControl
                key={filter.id}
                bgColor={settingBgColor}
                mb={4}
                p={4}
                borderRadius="md"
              >
                <Flex align="center" ml={4}>
                  <FormLabel  mb="0" flex="1" align="start">
                    {filter.label}
                    {isRequired(filter) && (
                      <Text as="span" color="red.500" ml={1}>
                        *
                      </Text>
                    )}
                  </FormLabel>
                  { filter.type === "toggle_array" && (
                    <Switch
                      isChecked={filterOptionEnabled[filter.id]}
                      onChange={() => {
                        const enabled = !filterOptionEnabled[filter.id];
                        setFilterOptionEnabled({
                          ...filterOptionEnabled,
                          [filter.id]: enabled,
                        });
                        if (!enabled) {
                          handleSettingChange(filter.id, []);
                        }
                      }}
                      colorScheme="blue"
                      mb={2}
                      mr={2}
                    />
                  )}
                </Flex>

                <CreatableSelect
                  options={selectOptions}
                  isMulti
                  placeholder="Enter values..."
                  value={currentValue}
                  onChange={(values) => handleSettingChange(filter.id, values ?? [])}
                  isDisabled={filter.type === "toggle_array" && !filterOptionEnabled[filter.id]}

                  // If we have predefined options, do NOT allow creation
                  isValidNewOption={(val) => {
                    if (hasOptions) return false;
                    if (!val) return false;
                    if (!filter.pattern) return true;
                    return filter.pattern.test(val);
                  }}

                  // FREEFORM MODE (comma/enter) for zipcode
                  inputValue={hasOptions ? undefined : inputValue}
                  onInputChange={
                    hasOptions
                      ? undefined
                      : (val, { action }) => {
                        setArrayInputValue(filter.id, val);

                        if (action === "input-change" && val.endsWith(",")) {
                          const newVal = val.slice(0, -1).trim();
                          return handleNewArrayValue(newVal, filter);
                        }
                        return val;
                      }
                  }
                  onKeyDown={
                    hasOptions
                      ? undefined
                      : (event) => {
                        if (event.key === "Enter") {
                          if (!inputValue) return;
                          handleNewArrayValue(inputValue, filter);
                          event.preventDefault();
                        }
                      }
                  }

                  getNewOptionData={(val, optionLabel) => ({
                    value: val,
                    label: optionLabel || val,
                  })}
                  formatCreateLabel={(val) => `Add "${val}"`}

                  styles={{
                    control: (base) => ({
                      ...base,
                      background: colorMode === "light" ? "gray.50" : "gray.700",
                      borderColor: colorMode === "light" ? "gray.300" : "gray.600",
                      "&:hover": {
                        borderColor: colorMode === "light" ? "gray.400" : "gray.500",
                      },
                      marginLeft:20,
                      marginRight:20
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: colorMode === "light" ? "white" : "gray.800",
                      boxShadow: "0px 10px 20px rgba(0,0,0,0.2)",
                      border: "1px solid",
                      borderColor: colorMode === "light" ? "gray.400" : "gray.500",
                      borderRadius: "4px",
                      zIndex: 9999,
                    }),
                    option: (base) => ({
                      ...base,
                      color: colorMode === "light" ? "black" : "white",
                    }),
                  }}
                />
              </FormControl>
            );
          }
          if (filter.type === "checkbox") {
            return (
              <FormControl
                key={filter.id}
                bgColor={settingBgColor}
                mb={4}
                p={4}
                borderRadius="md"
              >
                <Flex alignItems="center">
                  <FormLabel mb="0" flex="1" ml={4}>
                    {filter.label}
                    {isRequired(filter) && (
                      <Text as="span" color="red.500" ml={1}>
                        *
                      </Text>
                    )}
                  </FormLabel>
                  <Switch
                    id={filter.id}
                    isChecked={getTemplateValue(filter.id, template)}
                    onChange={(e) => handleSettingChange(filter.id, e.target.checked)}
                    size="lg"
                    ml={4}
                    mr={2}
                  />
                </Flex>
                {filter.description && <Flex ml={4} fontSize="sm" color="gray.500">
                  <Text>
                    {typeof filter.description === "function"
                      ? filter.description({ reminderExcludedOperatoriesCount })
                      : filter.description}
                  </Text>
                </Flex>}
              </FormControl>
            );
          }
          if (filter.type === "text") {
            return (
              <FormControl
                key={filter.id}
                bgColor={settingBgColor}
                mb={4}
                p={4}
                borderRadius="md"
              >
                <Flex align={"left"} direction="column" ml={4} mr={4}>
                  <FormLabel>
                    {filter.label}
                    {isRequired(filter) && (
                      <Text as="span" color="red.500" ml={1}>
                        *
                      </Text>
                    )}
                  </FormLabel>
                  <DebouncedInput
                    value={getTemplateValue(filter.id, template) ?? filter.defaultValue ?? ""}
                    onChange={(val) => handleSettingChange(filter.id, val)}
                    variant="filled"
                    bg={colorMode === "light" ? "gray.50" : "gray.700"}
                    borderColor={colorMode === "light" ? "gray.300" : "gray.600"}
                    _hover={{
                      bg: colorMode === "light" ? "gray.200" : "gray.600",
                    }}
                    _focus={{ borderColor: "blue.500" }}
                  />
                </Flex>
              </FormControl>
            );
          }
          if (filter.type === "textarea") {
            return (
              <FormControl
                key={filter.id}
                bgColor={settingBgColor}
                mb={4}
                p={4}
                borderRadius="md"
              >
                <Flex align={"left"} direction="column" ml={4} mr={4}>
                  <FormLabel>
                    {filter.label}
                    {isRequired(filter) && (
                      <Text as="span" color="red.500" ml={1}>
                        *
                      </Text>
                    )}
                  </FormLabel>
                  {filter.description && <Text fontSize="sm" color="gray.500" mb={2}>{filter.description}</Text>}
                  <Textarea
                    value={getTemplateValue(filter.id, template) ?? filter.defaultValue ?? ""}
                    onChange={(e) => handleSettingChange(filter.id, e.target.value)}
                    onFocus={() => handleFocus?.(filter.path || filter.id.split("."))}
                    onBlur={handleBlur}
                    variant="filled"
                    bg={colorMode === "light" ? "gray.50" : "gray.700"}
                    borderColor={colorMode === "light" ? "gray.300" : "gray.600"}
                    _hover={{
                      bg: colorMode === "light" ? "gray.200" : "gray.600",
                    }}
                    _focus={{ borderColor: "blue.500" }}
                    rows={3}
                  />
                </Flex>
              </FormControl>
            );
          }
          if (filter.type === "datetime") {
            return (
              <FormControl
                key={filter.id}
                align="start"
                bgColor={settingBgColor}
                mb={4}
                p={4}
                borderRadius="md"
              >
                <FormLabel ml={4}>
                  {filter.label}
                  {isRequired(filter) && (
                    <Text as="span" color="red.500" ml={1}>
                      *
                    </Text>
                  )}
                </FormLabel>
                <Box width="100%" ml={4}>
                  <ReactDatePicker
                    id={"date-time-picker"}
                    selected={getTemplateValue(filter.id, template) ? new Date(dateToDatePicker(getTemplateValue(filter.id, template))) : null}
                    onChange={(date) => handleSettingChange(filter.id, dateFromDatePicker(date))}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    timeCaption="time"
                    dateFormat="MMMM d, yyyy h:mm aa"
                    className="datepicker"
                    wrapperClassName={colorMode === "light" ? "light-mode" : "dark-mode"}
                    customInput={
                      <Input
                        variant="filled"
                        bg={colorMode === "light" ? "gray.50" : "gray.700"}
                      />
                    }
                  />
                </Box>
              </FormControl>
            );
          }
          if (filter.type === "radio") {
            return (
              <FormControl
                key={filter.id}
                bgColor={settingBgColor}
                mb={4}
                p={4}
                borderRadius="md"
              >
                <FormLabel ml={4}>
                  {filter.label}
                  {isRequired(filter) && (
                    <Text as="span" color="red.500" ml={1}>
                      *
                    </Text>
                  )}
                </FormLabel>
                <RadioGroup
                  onChange={(value) => handleSettingChange(filter.id, value)}
                  value={getTemplateValue(filter.id, template)}
                  ml={4}
                >
                  <Stack direction="row" spacing={5} align="left">
                    {filter.options.map((o) => (
                      <Radio key={o.value} value={o.value}>
                        {o.name}
                      </Radio>
                    ))}
                  </Stack>
                </RadioGroup>
              </FormControl>
            );
          }
          if (filter.type === "rangeDate") {
            return (
              <FormControl
                key={filter.id}
                bgColor={settingBgColor}
                mb={4}
                p={4}
                borderRadius="md"
              >
                <FormLabel ml={4}>
                  {filter.label}
                  {isRequired(filter) && (
                    <Text as="span" color="red.500" ml={1}>
                      *
                    </Text>
                  )}
                </FormLabel>
                <Flex align="left">
                  <Box flex="1" mr={2} align="start">
                    <FormLabel ml={4}>From</FormLabel>
                    <ReactDatePicker
                      selected={getTemplateValue(`${filter.id}`, template)[0] ? new Date(getTemplateValue(`${filter.id}`, template)[0]) : null}
                      onChange={(date) => handleSettingChange(`${filter.id}[0]`, date, "rangeDate")}
                      dateFormat="MMMM d, yyyy"
                      className="datepicker"
                      customInput={
                        <Input
                          variant="filled"
                          bg={colorMode === "light" ? "gray.50" : "gray.700"}
                          borderColor={colorMode === "light" ? "gray.300" : "gray.600"}
                          borderWeight="1px"
                          ml={4}
                        />
                      }
                    />
                  </Box>
                  <Box flex="1" align="start">
                    <FormLabel>To</FormLabel>
                    <ReactDatePicker
                      selected={getTemplateValue(`${filter.id}`, template)[1] ? new Date(getTemplateValue(`${filter.id}`, template)[1]) : null}
                      onChange={(date) => handleSettingChange(`${filter.id}[1]`, date, "rangeDate")}
                      dateFormat="MMMM d, yyyy"
                      className="datepicker"
                      customInput={
                        <Input
                          variant="filled"
                          bg={colorMode === "light" ? "gray.50" : "gray.700"}
                          borderColor={colorMode === "light" ? "gray.300" : "gray.600"}
                          borderWeight="1px"
                        />
                      }
                    />
                  </Box>
                </Flex>
              </FormControl>
            );
          }
          if (filter.type === "select") {
            return (
              <FormControl
                key={filter.id}
                bgColor={settingBgColor}
                mb={4}
                p={4}
                borderRadius="md"
              >
                <FormLabel ml={4}>
                  {filter.label}
                  {isRequired(filter) && (
                    <Text as="span" color="red.500" ml={1}>
                      *
                    </Text>
                  )}
                </FormLabel>
                <Flex ml={4} mr={2}>
                  <Select
                    placeholder={filter.title}
                    value={getTemplateValue(filter.id, template) ?? filter.defaultValue ?? ""}
                    onChange={(e) => handleSettingChange(`${filter.id}`, e.target.value, filter.type)}
                    mr={2}
                  >
                    {filter.options.map((o, i) => (
                      <option key={i} value={o.value}>
                        {o.name || o.label}
                      </option>
                    ))}
                  </Select>
                </Flex>
              </FormControl>
            );
          }
          if (filter.type === "facebook_campaign_select") {
            const currentFacebookCampaignId = getTemplateValue(filter.id, template) || "";
            const currentVivaCampaignId = template?._id;
            const assignedFacebookCampaignIds = new Set(
              (campaigns || [])
                .filter((campaign) =>
                  campaign.purpose === "lead-ads"
                  && !campaign.archived
                  && campaign._id !== currentVivaCampaignId
                  && campaign.source?.campaign_id
                )
                .map((campaign) => campaign.source.campaign_id)
            );
            const otherDefaultCampaign = (campaigns || []).find((campaign) =>
              isDefaultLeadAdsCampaign(campaign)
              && campaign._id !== currentVivaCampaignId
            );
            const facebookOptions = (facebookCampaignOptions || [])
              .filter((campaign) =>
                campaign.id === currentFacebookCampaignId
                || !assignedFacebookCampaignIds.has(campaign.id)
              );

            return (
              <FormControl
                key={filter.id}
                bgColor={settingBgColor}
                mb={4}
                p={4}
                borderRadius="md"
              >
                <FormLabel ml={4}>{filter.label}</FormLabel>
                {filter.description && (
                  <Flex ml={4} fontSize="sm" color="gray.500" mb={2}>
                    <Text>{filter.description}</Text>
                  </Flex>
                )}
                <Flex ml={4} mr={2}>
                  <Select
                    value={currentFacebookCampaignId}
                    onChange={(e) => {
                      const selected = facebookOptions.find((campaign) => campaign.id === e.target.value);
                      handleSettingChange("source.type", "facebook");
                      handleSettingChange("source.campaign_id", e.target.value);
                      handleSettingChange("source.campaign_name", selected?.name || "");
                      handleSettingChange("source.ad_account_id", selected?.ad_account_id || "");
                      handleSettingChange("source.is_default", !e.target.value);
                    }}
                  >
                    <option value="" disabled={!!otherDefaultCampaign && !template?.source?.is_default}>
                      {otherDefaultCampaign && !template?.source?.is_default
                        ? `Default already assigned to ${otherDefaultCampaign.name}`
                        : "Default for unassigned Facebook campaigns"}
                    </option>
                    {facebookOptions.map((campaign) => (
                      <option key={campaign.id} value={campaign.id}>
                        {[
                          campaign.name || campaign.id,
                          campaign.effective_status || campaign.status,
                          campaign.ad_account_name,
                        ].filter(Boolean).join(" - ")}
                      </option>
                    ))}
                  </Select>
                </Flex>
              </FormControl>
            );
          }
          if (filter.type === "toggle_select") {
            const currentValue = getTemplateValue(filter.id, template);
            const isEnabled = !!currentValue;
            return (
              <FormControl
                key={filter.id}
                bgColor={settingBgColor}
                mb={4}
                p={4}
                borderRadius="md"
              >
                <Flex alignItems="center">
                  <FormLabel mb="0" flex="1" ml={4}>
                    {filter.label}
                  </FormLabel>
                  <Switch
                    isChecked={isEnabled}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleSettingChange(filter.id, filter.options[0]?.value || "en");
                      } else {
                        handleSettingChange(filter.id, "");
                      }
                    }}
                    size="lg"
                    ml={4}
                    mr={2}
                  />
                </Flex>
                {filter.description && (
                  <Flex ml={4} fontSize="sm" color="gray.500">
                    <Text>{filter.description}</Text>
                  </Flex>
                )}
                {isEnabled && (
                  <Flex ml={4} mr={2} mt={3}>
                    <Select
                      value={currentValue}
                      onChange={(e) => handleSettingChange(filter.id, e.target.value)}
                      mr={2}
                    >
                      {filter.options.map((o, i) => (
                        <option key={i} value={o.value}>
                          {o.name}
                        </option>
                      ))}
                    </Select>
                  </Flex>
                )}
              </FormControl>
            );
          }
          if (filter.type === "rangeInt") {
            return (
              <FormControl
                key={filter.id}
                bgColor={settingBgColor}
                mb={4}
                p={4}
                borderRadius="md"
              >
                <FormLabel ml={4}>
                  {filter.label}
                  {isRequired(filter) && (
                    <Text as="span" color="red.500" ml={1}>
                      *
                    </Text>
                  )}
                </FormLabel>
                <Flex ml={4} mr={2}>
                  <Select
                    placeholder={filter.title || "From"}
                    value={(Array.isArray(getTemplateValue(filter.id, template)) ? getTemplateValue(filter.id, template) : [getTemplateValue(filter.id, template)])[0] ?? ""}
                    onChange={(e) => handleSettingChange(`${filter.id}[0]`, e.target.value, filter.type)}
                    mr={2}
                  >
                    {Array.from({ length: filter.range[0][1] - filter.range[0][0] + 1 }, (_, i) => i + filter.range[0][0]).map((age) => (
                      <option key={age} value={age}>
                        {age}
                      </option>
                    ))}
                  </Select>
                  {filter.range[1] && (
                    <>
                      <Select
                        placeholder={filter.title || "To"}
                        value={getTemplateValue(filter.id, template)[1] || ""}
                        onChange={(e) => handleSettingChange(`${filter.id}[1]`, e.target.value, filter.type)}
                      >
                        {Array.from({ length: filter.range[1][1] - filter.range[1][0] + 1 }, (_, i) => i + filter.range[1][0]).map((age) => (
                          <option key={age} value={age}>
                            {age}
                          </option>
                        ))}
                      </Select>
                    </>
                  )}
                </Flex>
              </FormControl>
            );
          }
          if (filter.type === "drip_steps") {
            return (
              <Box key={filter.id} mb={4} w="100%">
                <Text mb={1}>{filter.label || "Drip Steps"}</Text>
                {filter.description && <Text fontSize="sm" color="gray.500" mb={2}>{filter.description}</Text>}
                <DripStepsEditor
                  steps={getTemplateValue(filter.id, template) || []}
                  onChange={(newSteps) => handleSettingChange(filter.id, newSteps)}
                  handleFocus={handleFocus}
                  handleBlur={handleBlur}
                />
              </Box>
            );
          }
          if (filter.type === "number") {
            return (
              <Box key={filter.id} mb={4}>
                <Text mb={1}>{filter.label}</Text>
                {filter.description && <Text fontSize="sm" color="gray.500" mb={2}>{filter.description}</Text>}
                <Input
                  type="number"
                  value={getTemplateValue(filter.id, template) || ""}
                  onChange={(e) => handleSettingChange(filter.id, parseInt(e.target.value) || 0)}
                  w="120px"
                />
              </Box>
            );
          }
          return (
            <FormControl
              key={filter.id}
              bgColor={settingBgColor}
              mb={4}
              p={4}
              borderRadius="md"
            >
              <Flex align="center" ml={4}>
                <Text mb="0" flex="1" align="start">
                  {filter.label}
                </Text>
                <Switch
                  isChecked={filterOptionEnabled[filter.id]}
                  onChange={() => {
                    const enabled = !filterOptionEnabled[filter.id];
                    setFilterOptionEnabled({
                      ...filterOptionEnabled,
                      [filter.id]: enabled,
                    });
                    if (!enabled) {
                      handleSettingChange(filter.id, []);
                    }
                  }}
                  colorScheme="blue"
                  mb={2}
                  mr={2}
                />
              </Flex>
              <RadioGroup
                onChange={(value) => handleSettingChange(`${filter.id}.is_included`, value === "include")}
                value={getTemplateValue(filter.id + ".is_included", template) ? "include" : "exclude"}
                isDisabled={!filterOptionEnabled[filter.id]}
                ml={4}
              >
                <Stack direction="row" spacing={5} align="left">
                  <Radio value="include">Include</Radio>
                  <Radio value="exclude">Exclude</Radio>
                </Stack>
              </RadioGroup>
              <Box mt={2} ml={4} mr={2}>
                <AsyncSelect
                  isMulti
                  cacheOptions
                  defaultOptions={true}
                  value={getTemplateValue(filter.id + ".list", template) || []}
                  loadOptions={loadOptions(filter.id)}
                  onChange={(selectedOptions) => handleSettingChange(filter.id + ".list", selectedOptions.map((option) => ({ value: option.value, label: option.label })))}
                  isDisabled={!filterOptionEnabled[filter.id]}
                  menuPortalTarget={document.body}
                  styles={{
                    control: (base) => ({
                      ...base,
                      background: colorMode === "light" ? "gray.50" : "gray.700",
                      borderColor: colorMode === "light" ? "gray.300" : "gray.600",
                      "&:hover": {
                        borderColor: colorMode === "light" ? "gray.400" : "gray.500",
                      },
                      "&:disabled": {
                        background: colorMode === "light" ? "gray.200" : "gray.600",
                      },
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: colorMode === "light" ? "white" : "gray.800",
                      boxShadow: "0px 10px 20px rgba(0,0,0,0.2)",
                      border: "1px solid",
                      borderColor: colorMode === "light" ? "gray.400" : "gray.500",
                      borderRadius: "4px",
                      zIndex: 9999,
                    }),
                    option: (base) => ({
                      ...base,
                      color: colorMode === "light" ? "black" : "white",
                    }),
                  }}
                />
              </Box>
            </FormControl>
          );
        })}
      <Flex width="100%" justifyContent="space-between" mb={4}>
        {!isEditing && campaignType !== 'lead-ads' && (
          <Button onClick={onBackClicked}>
            Back to Template
          </Button>
        )}
        <Spacer />
        <Flex align="right">
          <Button marginRight="10px" onClick={onCancelClicked}>
            Cancel
          </Button>
          <Button colorScheme="green" onClick={onSaveClicked} isDisabled={savingCampaign}>
            {savingCampaign ? <Spinner /> : "Save"}
          </Button>
        </Flex>
      </Flex>
    </VStack>
  );
};

export default React.memo(CampaignSettings);
